// One car's live camera on the Live Fleet page.
//
// The stream is asked for through fleet/startCameraStream, which records the
// viewing, then polled through fleet/pollCameraStream until Cartrack answers.
// The video itself comes from our own relay (/api/_camera/..., see
// api/src/runtime/cameraProxy.ts), never from Cartrack directly: Cartrack's
// video host refuses the app's origin, and the relay keeps its key server-side.
// Every request to the relay carries the signed-in login, which is why this
// uses hls.js with an Authorization header rather than a bare <video src>.
//
// Sound is off unless the manager turns it on, and the page lets only one tile
// have sound at a time: two cabins talking over each other is not a feature.
import { useCallback, useEffect, useRef, useState } from 'react'
import type Hls from 'hls.js'
import { Maximize2, Minimize2, RefreshCw, Video, VideoOff, Volume2, VolumeX } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { usePollCameraStream, useStartCameraStream } from '../../hooks/backend/fleet'
import { cn } from '../../lib/shadcn/utils'
import {
  CAMERA_STATUS_LABEL,
  canStream,
  channelList,
  defaultChannel,
  idleReason,
  renewInMs,
  type CameraChannel,
  type CameraStatus,
  type StreamPoll,
} from '../../utils/liveCamera'

type Props = {
  vehicleId: string
  plate: string | null
  subtitle: string | null
  cameraStatus: CameraStatus
  channels: CameraChannel[]
  canWatch: boolean
  /** False while the page is hidden or paused for idleness. */
  active: boolean
  /** Staggers the first request so a grid of cars does not ask all at once. */
  startDelayMs: number
  soundOn: boolean
  onToggleSound: () => void
  large: boolean
  onToggleLarge: () => void
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'live'; src: string; expiresAt: string | null }
  | { kind: 'failed'; message: string }

// Cartrack took 9 to 17 seconds to answer on 30 September, so the first poll
// waits and the rest are spaced: fifteen tiles at 1.5 seconds was ten requests
// a second against a 60 connection database.
const FIRST_POLL_MS = 5000
const POLL_MS = 2500

// A dropped connection (503, transient) on start or poll is retried here even
// though both are writes: polling only records Cartrack's answer, and a second
// start within 45 seconds returns the same pending session, so neither can do
// anything twice.
function isTransient(err: unknown): boolean {
  return typeof err === 'object' && err !== null && [0, 503].includes((err as { status?: number }).status ?? -1)
}
const wait = (ms: number) => new Promise((res) => setTimeout(res, ms))

// hls.js is about 570 KB, so it is fetched the first time a camera plays rather
// than in the bundle every page of the cockpit downloads.
let hlsModule: Promise<typeof import('hls.js').default> | null = null
function loadHls() {
  hlsModule ??= import('hls.js').then((m) => m.default)
  return hlsModule
}

export function CameraTile(props: Props) {
  const { vehicleId, cameraStatus, channels, canWatch, active, startDelayMs, soundOn } = props
  const [channel, setChannel] = useState(() => defaultChannel(channels))
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [attempt, setAttempt] = useState(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const hlsRef = useRef<Hls | null>(null)
  const tokenRef = useRef<string>('')
  // Read once. The delay only staggers the first request; as an effect
  // dependency it restarted every stream whenever the grid's order changed,
  // which on 30 September reopened each car's camera every 40 seconds.
  const startDelayRef = useRef(startDelayMs)

  const start = useStartCameraStream<{ sessionId: string | null }>()
  const poll = usePollCameraStream<StreamPoll>()
  const startTrigger = start.trigger
  const pollTrigger = poll.trigger

  const reason = idleReason(cameraStatus, canWatch)
  const wanted = active && reason === null && canStream(cameraStatus)

  // Keep a current token for the relay. supabase-js renews the session itself;
  // this only reads what it holds.
  useEffect(() => {
    let cancelled = false
    const read = () =>
      supabase.auth.getSession().then(({ data }) => {
        if (!cancelled) tokenRef.current = data.session?.access_token ?? ''
      })
    void read()
    const id = window.setInterval(read, 30_000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [])

  // Ask for a stream, wait for Cartrack, and hand back the relay path.
  const open = useCallback(
    async (isCancelled: () => boolean): Promise<Phase> => {
      let s: { sessionId: string | null } | undefined
      for (let tries = 0; ; tries++) {
        try {
          s = await startTrigger({ vehicleId, cameras: [channel] }, { skipCache: true })
          break
        } catch (err) {
          if (!isTransient(err) || tries >= 2 || isCancelled()) throw err
          await wait(2000 + Math.random() * 2000)
        }
      }
      const sessionId = s?.sessionId
      if (!sessionId) return { kind: 'failed', message: 'The stream could not be started.' }
      await wait(FIRST_POLL_MS)
      const deadline = Date.now() + 80_000
      while (Date.now() < deadline) {
        if (isCancelled()) return { kind: 'idle' }
        let r: StreamPoll | undefined
        try {
          r = await pollTrigger({ sessionId }, { skipCache: true })
        } catch (err) {
          if (!isTransient(err)) throw err
        }
        if (r && !r.pending) {
          const live = r.streams.find((x) => x.camera === channel) ?? r.streams[0]
          if (r.outcome === 'live' && live) return { kind: 'live', src: live.src, expiresAt: live.expires_at }
          const failed = r.failed.find((f) => f.camera === channel)
          return { kind: 'failed', message: failed?.message ?? r.error ?? 'Cartrack could not stream this camera.' }
        }
        await wait(POLL_MS)
      }
      return { kind: 'failed', message: 'Cartrack did not answer in time. Try again shortly.' }
    },
    [startTrigger, pollTrigger, vehicleId, channel],
  )

  // Start when wanted, stop when not. A change of channel or a retry restarts.
  useEffect(() => {
    if (!wanted) {
      setPhase({ kind: 'idle' })
      return
    }
    let cancelled = false
    const isCancelled = () => cancelled
    const timer = window.setTimeout(() => {
      setPhase({ kind: 'starting' })
      open(isCancelled)
        .then((p) => {
          if (!cancelled) setPhase(p)
        })
        .catch((err: unknown) => {
          if (!cancelled) setPhase({ kind: 'failed', message: err instanceof Error ? err.message : String(err) })
        })
    }, attempt === 0 ? startDelayRef.current : 0)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [wanted, open, attempt])

  // Renew before Cartrack's link expires. The player swaps to the new stream
  // without tearing down, so the picture carries on.
  useEffect(() => {
    if (phase.kind !== 'live') return
    let cancelled = false
    const timer = window.setTimeout(() => {
      open(() => cancelled)
        .then((p) => {
          if (!cancelled && p.kind !== 'idle') setPhase(p)
        })
        .catch(() => {
          // Keep playing the old stream until it actually stops; the error
          // handler below shows the failure if it does.
        })
    }, renewInMs(phase.expiresAt))
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [phase, open])

  // Attach the player to the relay path.
  const liveSrc = phase.kind === 'live' ? phase.src : null
  useEffect(() => {
    const video = videoRef.current
    if (!video || !liveSrc) return
    let cancelled = false
    void loadHls().then((Hls) => {
    if (cancelled) return
    if (!Hls.isSupported()) {
      setPhase({
        kind: 'failed',
        message: 'This browser cannot play the camera here. Use a current Chrome, Edge, Firefox or Safari.',
      })
      return
    }
    let hls = hlsRef.current
    if (!hls) {
      hls = new Hls({
        liveSyncDurationCount: 2,
        lowLatencyMode: false,
        xhrSetup: (xhr) => {
          if (tokenRef.current) xhr.setRequestHeader('Authorization', `Bearer ${tokenRef.current}`)
        },
      })
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return
        const res = data.response as { code?: number; text?: string } | undefined
        const status = res?.code
        // Say what actually failed. "The car may have lost signal" alone hid a
        // relay fault on the first day; the code and the relay's own sentence
        // make the next screenshot enough to diagnose it.
        const said = typeof res?.text === 'string' && res.text.length < 200 ? res.text.trim() : ''
        setPhase({
          kind: 'failed',
          message:
            status === 410 || status === 403
              ? 'This stream has ended or you can no longer watch this car.'
              : status
                ? `The video stopped (${status}${said ? `: ${said}` : ''}).`
                : `The video stopped (${data.details}). The car may have lost signal.`,
        })
      })
      // Play again once the playlist is in: a play() issued before the media
      // source exists is dropped by some browsers, and autoPlay alone does not
      // always recover from that on a swapped source.
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        void video.play().catch(() => {})
      })
      hls.attachMedia(video)
      hlsRef.current = hls
    }
    hls.loadSource(liveSrc)
    void video.play().catch(() => {
      // Autoplay is allowed muted; if a browser still refuses, the controls let
      // the manager press play.
    })
    })
    return () => {
      cancelled = true
    }
  }, [liveSrc])

  // Tear the player down whenever the tile is not live.
  useEffect(() => {
    if (phase.kind === 'live') return
    hlsRef.current?.destroy()
    hlsRef.current = null
  }, [phase.kind])
  useEffect(() => () => hlsRef.current?.destroy(), [])

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = !soundOn
  }, [soundOn, phase.kind])

  const offered = channelList(channels)

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-lg border border-border bg-card',
        props.large && 'sm:col-span-2 sm:row-span-2',
      )}
    >
      <div className="relative aspect-video bg-black">
        {phase.kind === 'live' && (
          <video ref={videoRef} className="h-full w-full object-contain" muted={!soundOn} autoPlay playsInline />
        )}
        {phase.kind !== 'live' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center text-xs text-white/80">
            {phase.kind === 'starting' ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span>Asking Cartrack for the camera…</span>
              </>
            ) : phase.kind === 'failed' ? (
              <>
                <VideoOff className="h-5 w-5" />
                <span>{phase.message}</span>
                <button
                  type="button"
                  onClick={() => setAttempt((a) => a + 1)}
                  className="mt-1 rounded border border-white/40 px-2 py-0.5 text-white hover:bg-white/10"
                >
                  Try again
                </button>
              </>
            ) : (
              <>
                <VideoOff className="h-5 w-5" />
                <span>{reason ?? (active ? 'Waiting to start' : 'Paused')}</span>
              </>
            )}
          </div>
        )}
        {phase.kind === 'live' && (
          <span className="absolute left-2 top-2 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Live
          </span>
        )}
        <div className="absolute right-2 top-2 flex gap-1">
          <button
            type="button"
            onClick={props.onToggleSound}
            disabled={phase.kind !== 'live'}
            aria-label={soundOn ? 'Turn sound off' : 'Turn sound on'}
            title={soundOn ? 'Turn sound off' : 'Turn sound on'}
            className="rounded bg-black/60 p-1 text-white hover:bg-black/80 disabled:opacity-40"
          >
            {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={props.onToggleLarge}
            aria-label={props.large ? 'Smaller' : 'Larger'}
            title={props.large ? 'Smaller' : 'Larger'}
            className="hidden rounded bg-black/60 p-1 text-white hover:bg-black/80 sm:block"
          >
            {props.large ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2">
        <Video className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="font-mono text-sm font-semibold text-foreground">{props.plate ?? 'No plate'}</span>
        <span className="truncate text-xs text-muted-foreground">{props.subtitle}</span>
        <span className="ml-auto flex shrink-0 gap-1">
          {offered.map((c) => (
            <button
              key={c.camera}
              type="button"
              onClick={() => {
                setChannel(c.camera)
                setAttempt((a) => a + 1)
              }}
              disabled={!wanted}
              title={`Camera ${c.camera}${c.online ? '' : ' (reported offline)'}`}
              className={cn(
                'h-6 min-w-6 rounded border px-1 text-[11px] font-medium tabular-nums disabled:opacity-40',
                c.camera === channel
                  ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))] text-white'
                  : c.online
                    ? 'border-border text-foreground hover:bg-muted'
                    : 'border-dashed border-border text-muted-foreground',
              )}
            >
              {c.camera}
            </button>
          ))}
        </span>
      </div>
      <p className="px-3 pb-2 text-[11px] text-muted-foreground">{CAMERA_STATUS_LABEL[cameraStatus]}</p>
    </div>
  )
}
